import React, { PureComponent } from 'react';
import { Exception, NotPlayableException } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { PlaybackInformation } from './PlaybackInformation/PlaybackInformation';
import THEOPlayer, { PLAYER_EVENTS } from '../../models/THEOPlayer';
import { generateBasicInfoForPlaylistItem, track } from '../../utils/Analytics';
import { AnalyticsEvent } from '../../utils/AnalyticsEvent';
import { AnalyticsSeverity } from '../../utils/AnalyticsSeverity';
import MoviePlaylistItem from '../../models/MoviePlaylistItem';
import ChannelPlaylistItem from '../../models/ChannelPlaylistItem';
import { withUserContext } from '../../contexts/UserContext';
import { PlaybackType } from './PlaybackType';
import { PlaybackState } from './PlaybackState';
import { CastState } from '../../models/CastState';
import { DEFAULT_VOLUME } from '../../config/constants';

const { CRITICAL, MAJOR, MINOR } = AnalyticsSeverity;
const { VOD_ERROR, CHANNEL_ERROR } = AnalyticsEvent;

const { HLS, DASH } = PlaybackType;

const HIDE_TIMEOUT = 6000;

class Player extends PureComponent {
	hideTimeout = null;
	playerElement = React.createRef();
	constructor(props) {
		super(props);

		this.state = {
			playbackState: PlaybackState.IDLE,
			position: 0,
			duration: 0,
			currentProgramDateTime: null,
			playbackInformationVisible: false,
			player: null,
			hasInitialized: false,
		};
	}

	componentDidMount() {
		this.initializePlayer();
	}

	async resetPlayer() {
		const { player } = this.state;

		return new Promise((resolve) => {
			localStorage.setItem('volume', player.getVolume());
			player.stop();
			this.setState(
				{
					playbackState: PlaybackState.IDLE,
					position: 0,
					duration: 0,
					currentProgramDateTime: null,
					playbackInformationVisible: false,
					hasInitialized: false,
				},
				() => {
					resolve();
				}
			);
		});
	}

	async componentDidUpdate(oldProps, oldState) {
		const { playlistItem } = this.props;
		const { playbackState, playbackInformationVisible, player } = this.state;

		if (!player) {
			this.initializePlayer();
		}

		if (playlistItem !== oldProps.playlistItem) {
			this.resetPlayer();
		}
		if (player && !oldState.player) {
			this.registerPlayerEventHandlers();
		}

		const { PLAYING, PAUSED } = PlaybackState;

		if (playbackState !== oldState.playbackState) {
			if (playbackState === PAUSED) {
				this.stopHideTimeout();
			} else if (playbackState === PLAYING && playbackInformationVisible) {
				this.startHideTimeout();
			}
		}

		if (playbackInformationVisible !== oldState.playbackInformationVisible) {
			if (playbackInformationVisible && playbackState === PLAYING) {
				this.startHideTimeout();
			} else {
				this.stopHideTimeout();
			}
		}
	}

	componentWillUnmount() {
		this.stop();
	}

	startHideTimeout() {
		this.stopHideTimeout();
		this.hideTimeout = setTimeout(() => {
			this.setState({
				playbackInformationVisible: false,
			});
		}, HIDE_TIMEOUT);
	}

	stopHideTimeout() {
		if (this.hideTimeout) {
			clearTimeout(this.hideTimeout);
		}
	}

	initializePlayer() {
		if (!this.playerElement.current) return;

		try {
			const player = new THEOPlayer(this.playerElement.current);
			player.setAutoplay(true);
			this.setState({
				player,
			});
		} catch (exception) {
			this.onPlayerError(exception);
		}
	}

	initializeVolume() {
		const { player } = this.state;
		const volumeLevel = localStorage.getItem('volume');

		if (volumeLevel === null || volumeLevel < 0 || volumeLevel > 1) {
			player.setVolume(DEFAULT_VOLUME);
			return;
		}
		player.setVolume(localStorage.getItem('volume'));
	}

	registerPlayerEventHandlers() {
		const { player } = this.state;
		const { PLAYING, PAUSE, PLAY, WAITING, TIME_UPDATE, ERROR, CAN_PLAY, SEEKED, SEEKING, ENDED } = PLAYER_EVENTS;

		player.addEventListener(ERROR, this.onPlayerError);
		player.addEventListener(PLAYING, this.onPlaybackStarted);
		player.addEventListener(SEEKING, this.onPlaybackBuffering);
		player.addEventListener(SEEKED, this.onSeekFinished);
		player.addEventListener(PAUSE, this.onPlaybackPaused);
		player.addEventListener(PLAY, this.onPlaybackRequested);
		player.addEventListener(WAITING, this.onPlaybackBuffering);
		player.addEventListener(TIME_UPDATE, this.onTimeUpdate);
		player.addEventListener(CAN_PLAY, this.onPlaybackInitialized);
		player.addEventListener(ENDED, this.onPlaybackStopped);

		player.addNetworkResponseInterceptor(this.onNetworkResponse);
	}

	onPlaybackStateChange(newState) {
		const { playbackState } = this.state;
		const { onPlaybackStateChange } = this.props;

		if (newState !== playbackState) {
			if (onPlaybackStateChange) onPlaybackStateChange(newState);

			this.setState({
				playbackState: newState,
			});
		}
	}

	onTimeUpdate = () => {
		const { onTimeUpdate } = this.props;
		const { player } = this.state;
		try {
			const timeInfo = {
				position: player.getPosition(),
				duration: player.getDuration(),
				currentProgramDateTime: player.getCurrentProgramDateTime(),
			};

			this.setState(timeInfo);

			if (onTimeUpdate) {
				onTimeUpdate(timeInfo);
			}
		} catch (error) {}
	};

	onPlaybackInitialized = () => {
		const { player, hasInitialized } = this.state;
		const { positionToPlayFrom } = this.props;
		if (!player || !hasInitialized) {
			this.setState({
				hasInitialized: true,
			});
			player.setMuted(false);
			this.initializeVolume();
			if (positionToPlayFrom > 0 && player.getPosition() === 0) {
				player.setPosition(positionToPlayFrom);
			}
		}
	};

	async play() {
		const { player } = this.state;
		this.onPlaybackStateChange(PlaybackState.LOADING);
		const { positionToPlayFrom, playlistItem, entitlement, isReady, user, device } = this.props;
		if (player && isReady) {
			try {
				const ads = [];
				const vastUrl = playlistItem.getVastUrl();
				if (vastUrl) {
					ads.push({
						skipOffset: 10,
						sources: vastUrl,
						timeOffset: 'start',
					});
				}

				const sources = [];
				const hlsUrl = await playlistItem.getUrl(HLS);
				const dashUrl = await playlistItem.getUrl(DASH);

				if (hlsUrl) {
					sources.push({
						src: hlsUrl,
						type: 'application/x-mpegurl',
						contentProtection: {
							integration: 'conax',
							fairplay: {
								certificateURL: 'https://fairplay.tv.c.is/certificates/F8E07191E52249F0CCA6EE541B048F89F369B1D5',
								licenseAcquisitionURL: 'https://fairplay.tv.c.is/license',
								headers: {
									PreAuthorization: entitlement.token,
								},
							},
						},
					});
				} else {
					throw new NotPlayableException('HLS URL missing');
				}

				if (dashUrl) {
					sources.push({
						src: dashUrl,
						type: 'application/dash+xml',
						contentProtection: {
							widevine: {
								licenseAcquisitionURL: 'https://widevine.tv.c.is/license',
								headers: {
									PreAuthorization: entitlement.token,
								},
							},
							deviceId: device.id,
							accountId: user.id,
							contentId: playlistItem.getId(),
							deviceAuth: user.accessToken,
						},
					});
				} else {
					throw new NotPlayableException('DASH URL missing');
				}

				if (sources && sources.length > 0) {
					if (positionToPlayFrom > 0) {
						player.setPosition(positionToPlayFrom);
					}
					player.setSources(sources, ads);
				} else {
					throw new NotPlayableException('No playable URLs');
				}
				player.setVolume(localStorage.getItem('volume'));
			} catch (error) {
				this.onPlayerError(error);
			}
		}
	}

	stop() {
		const { player } = this.state;
		this.stopHideTimeout();

		try {
			if (player) {
				localStorage.setItem('volume', player.getVolume());
				player.destroy();
			}
		} catch (exception) {}
	}

	onPlaybackRequested = () => {
		const { hasInitialized } = this.state;
		const { PLAYING } = PlaybackState;
		if (hasInitialized) {
			this.onPlaybackStateChange(PLAYING);
		}
	};

	onSeekFinished = () => {
		const { onPositionChanged } = this.props;
		const { player } = this.state;
		const { PLAYING, PAUSED } = PlaybackState;

		if (player.isPaused()) {
			this.onPlaybackStateChange(PAUSED);
		} else {
			this.onPlaybackStateChange(PLAYING);
		}

		if (onPositionChanged) {
			const timeInfo = {
				position: player.getPosition(),
				duration: player.getDuration(),
				currentProgramDateTime: player.getCurrentProgramDateTime(),
			};

			onPositionChanged(timeInfo);
		}
	};

	onPlaybackStarted = () => {
		this.onPlaybackStateChange(PlaybackState.PLAYING);
	};

	onPlaybackBuffering = () => {
		const { hasInitialized } = this.state;
		if (hasInitialized) {
			this.onPlaybackStateChange(PlaybackState.BUFFERING);
		} else {
			this.onPlaybackStateChange(PlaybackState.LOADING);
		}
	};

	onPlaybackPaused = () => {
		this.onPlaybackStateChange(PlaybackState.PAUSED);
	};

	onPlaybackStopped = () => {
		const { player } = this.state;
		player.disablePIP();
		this.onPlaybackStateChange(PlaybackState.IDLE);
	};

	onNetworkResponse = async ({ status, statusText, url, request, body }) => {
		const { type } = request;

		if (status < 200 || status > 299) {
			let errorMessage = `${status}`;

			if (statusText) {
				errorMessage = `${errorMessage} ${statusText}`;
			}

			errorMessage = `${errorMessage} - ${url}`;

			let errorName = 'UnknownRequestError';
			let severity = MINOR;

			if (type) {
				severity = MAJOR;

				switch (type) {
					case 'manifest':
						errorName = 'ManifestRequestError';
						break;
					case 'segment':
						errorName = 'SegmentRequestError';
						break;
					case 'content-protection':
						errorName = 'LicenseRequestError';
						if (window.TextDecoder && body instanceof ArrayBuffer) {
							const licenseResponse = new TextDecoder().decode(body);
							if (licenseResponse) {
								errorMessage = `${licenseResponse} - ${errorMessage}`;
							}
						}
						break;
					default:
						errorName = `${type.charAt(0).toUpperCase() + type.substring(1)}RequestError`;
						break;
				}
			}

			this.trackError(errorName, errorMessage, severity);
		}
	};

	trackError(errorName, errorMessage, errorSeverity = CRITICAL) {
		const { playlistItem } = this.props;

		const properties = {
			...generateBasicInfoForPlaylistItem(playlistItem),
			errorName,
			errorMessage,
			errorSeverity,
			errorPosition: playlistItem.lastKnownPosition,
		};

		if (playlistItem instanceof ChannelPlaylistItem) {
			track(CHANNEL_ERROR, properties);
		} else if (playlistItem instanceof MoviePlaylistItem) {
			track(VOD_ERROR, properties);
		}
	}

	notifyError(error) {
		if (error instanceof Exception) {
			this.trackError(error.name, error.message);
		} else {
			this.trackError('PlaybackFailedException', error.errorObject?.message);
		}
	}

	onPlayerError = (error) => {
		this.notifyError(error);
		this.onPlaybackError(error);
	};

	onPlaybackError = (error) => {
		const { onPlaybackError } = this.props;

		if (onPlaybackError) {
			onPlaybackError(error);
		}

		this.onPlaybackStateChange(PlaybackState.IDLE);
	};

	onMouseMove = () => {
		const { playbackStartingState } = this.props;
		const { hasInitialized } = this.state;

		if (playbackStartingState !== null || !hasInitialized) return;

		this.setState({
			playbackInformationVisible: true,
		});

		this.startHideTimeout();
	};

	onMouseLeave = () => {
		const { playbackInformationVisible } = this.state;

		if (playbackInformationVisible) {
			this.setState({
				playbackInformationVisible: false,
			});
		}
	};

	toggleVisibility() {
		const { playbackInformationVisible, hasInitialized } = this.state;
		const { playbackStartingState } = this.props;

		if (playbackStartingState !== null || !hasInitialized) return;

		this.setState({
			playbackInformationVisible: !playbackInformationVisible,
		});
	}

	onTouchStart = (event) => {
		event.preventDefault();
		this.toggleVisibility();
	};

	onTouchEnd = (event) => {
		event.preventDefault();
	};

	onKeyDown = () => {
		const { playbackStartingState } = this.props;
		const { hasInitialized } = this.state;

		if (playbackStartingState !== null || !hasInitialized) return;

		this.setState({
			playbackInformationVisible: true,
		});

		this.startHideTimeout();
	};

	onDoubleClick = (event) => {
		const { player } = this.state;
		event.preventDefault();
		player.toggleFullscreen();
	};

	onClick = (event) => {
		event.preventDefault();
		this.toggleVisibility();
	};

	render() {
		const { onCloseRequested } = this.props;
		const { player, position, duration, currentProgramDateTime, playbackState, playbackInformationVisible } = this.state;
		return (
			<div
				role="presentation"
				className={`player-container${playbackInformationVisible ? ' playback-information-visible' : ''}`}
				ref={this.playerElement}
				onTouchStart={this.onTouchStart}
				onTouchEnd={this.onTouchEnd}
				onMouseLeave={this.onMouseLeave}
				onMouseMove={this.onMouseMove}
				onKeyDown={this.onKeyDown}
				onTouchMove={this.onMouseMove}
				onDoubleClick={this.onDoubleClick}
				onClick={this.onClick}
			>
				<div className={`chromecast-playback${player && player.isCasting() && player.getChromecastState() === CastState.CONNECTED ? ' visible' : ''}`}>
					<div className={'chromecast-info-wrapper'}>
						<div className={'chromecast-title-wrapper'}>
							<div className={'chromecast-icon'} />
							<div className={'chromecast-playback-title'}>Í spilun á</div>
						</div>
						<div className={'chromecast-playback-subtitle'}>{(player && player.getChromecastName()) || 'Chromecast'}</div>
					</div>
				</div>
				<PlaybackInformation
					player={player}
					playbackState={playbackState}
					date={currentProgramDateTime}
					position={position}
					duration={duration}
					visible={playbackInformationVisible}
					onShowPlaybackControlsRequested={this.onMouseMove}
					onCloseRequested={onCloseRequested}
				/>
			</div>
		);
	}
}

export default withUserContext(Player);
